import React from "react"
import { z } from "zod"

import type { UpdateProjectArgs } from "@productlane/zero/types"
import {
	Button,
	Dialog,
	Form,
	FormSelect,
	FormTextarea,
	useZodForm,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { ColorPicker } from "@/components/color-picker"
import { analytics } from "@/lib/analytics"
import { mutateOk, useZero } from "@/lib/zero"

interface UpdateProjectDialogProps {
	isOpen: boolean
	onOpenChange: (value: boolean) => void
	values: UpdateProjectFormValues
}

const UpdateProjectFormSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1),
	description: z.string().optional(),
	color: z.string().min(1),
	icon: z.string().nullable(),
	teamId: z.string().min(1),
	status: z.string().min(1),
})
type UpdateProjectFormValues = z.infer<typeof UpdateProjectFormSchema>

export const UpdateProjectDialog = React.memo(
	({ isOpen, onOpenChange, values }: UpdateProjectDialogProps) => {
		return (
			<Dialog
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				title="Update project"
				footer={
					<Button form="update-project-form" type="submit">
						Update
					</Button>
				}
			>
				<Inner
					isOpen={isOpen}
					onOpenChange={onOpenChange}
					values={values}
					key={String(isOpen)}
				/>
			</Dialog>
		)
	},
)

type InnerProps = UpdateProjectDialogProps

const Inner = React.memo(({ onOpenChange, values }: InnerProps) => {
	const zero = useZero()
	const form = useZodForm({
		schema: UpdateProjectFormSchema,
		defaultValues: {
			...values,
		},
	})
	const colorWatch = form.watch("color")
	const iconWatch = form.watch("icon")
	return (
		<Form
			id="update-project-form"
			form={form}
			className="space-y-4"
			onSubmit={async (formValues) => {
				const updatePayload: UpdateProjectArgs = {
					id: values.id,
					color: formValues.color,
					name: formValues.name,
					description: formValues.description,
					state: formValues.status,
				}
				if (formValues.icon) {
					updatePayload.icon = formValues.icon
				}
				await mutateOk(zero.mutate(mutators.projects.update(updatePayload)))

				onOpenChange(false)
				analytics.track("update-project", { id: values.id })
			}}
		>
			<FormTextarea name="name" label="Name" minRows={2} />
			<FormTextarea name="description" label="Description" minRows={2} />
			<div className="flex items-start gap-4">
				<ColorPicker
					withInputs={false}
					value={colorWatch}
					onChange={(color) => {
						form.setValue("color", color)
					}}
					withIcon
					withEmoji
					icon={iconWatch}
					onIconChange={(icon) => {
						form.setValue("icon", icon)
					}}
				/>
				<FormSelect
					name="status"
					placeholder="Select status..."
					className="flex-1"
					options={[
						{ label: "Completed", value: "completed" },
						{ label: "Started", value: "started" },
						{ label: "Planned", value: "planned" },
						{ label: "Backlog", value: "backlog" },
						{ label: "Closed", value: "closed" },
					]}
				/>
			</div>
		</Form>
	)
})
